/**
 * BiblePageBackground — full-screen aged Bible page beneath the devotional.
 * Real KJV passages filling the whole viewport in flowing two-column text.
 * Slightly heavier than before — reads as soft Bible page texture across
 * the entire screen, never overpowering the focal content.
 */
const PASSAGES = [
  { ch: "Psalm 23", verses: [
    "The Lord is my shepherd; I shall not want.",
    "He maketh me to lie down in green pastures: he leadeth me beside the still waters.",
    "He restoreth my soul: he leadeth me in the paths of righteousness for his name's sake.",
    "Yea, though I walk through the valley of the shadow of death, I will fear no evil: for thou art with me; thy rod and thy staff they comfort me.",
    "Thou preparest a table before me in the presence of mine enemies: thou anointest my head with oil; my cup runneth over.",
    "Surely goodness and mercy shall follow me all the days of my life: and I will dwell in the house of the Lord for ever.",
  ]},
  { ch: "John 14", verses: [
    "Let not your heart be troubled: ye believe in God, believe also in me.",
    "In my Father's house are many mansions: if it were not so, I would have told you. I go to prepare a place for you.",
    "And if I go and prepare a place for you, I will come again, and receive you unto myself; that where I am, there ye may be also.",
    "And whither I go ye know, and the way ye know.",
    "Thomas saith unto him, Lord, we know not whither thou goest; and how can we know the way?",
    "Jesus saith unto him, I am the way, the truth, and the life: no man cometh unto the Father, but by me.",
  ]},
  { ch: "Romans 8", verses: [
    "Who shall separate us from the love of Christ? shall tribulation, or distress, or persecution, or famine, or nakedness, or peril, or sword?",
    "Nay, in all these things we are more than conquerors through him that loved us.",
    "For I am persuaded, that neither death, nor life, nor angels, nor principalities, nor powers, nor things present, nor things to come,",
    "Nor height, nor depth, nor any other creature, shall be able to separate us from the love of God, which is in Christ Jesus our Lord.",
  ]},
  { ch: "Isaiah 40", verses: [
    "Hast thou not known? hast thou not heard, that the everlasting God, the Lord, the Creator of the ends of the earth, fainteth not, neither is weary? there is no searching of his understanding.",
    "He giveth power to the faint; and to them that have no might he increaseth strength.",
    "Even the youths shall faint and be weary, and the young men shall utterly fall:",
    "But they that wait upon the Lord shall renew their strength; they shall mount up with wings as eagles; they shall run, and not be weary; and they shall walk, and not faint.",
  ]},
  { ch: "Philippians 4", verses: [
    "Be careful for nothing; but in every thing by prayer and supplication with thanksgiving let your requests be made known unto God.",
    "And the peace of God, which passeth all understanding, shall keep your hearts and minds through Christ Jesus.",
    "Finally, brethren, whatsoever things are true, whatsoever things are honest, whatsoever things are just, whatsoever things are pure, whatsoever things are lovely, whatsoever things are of good report; if there be any virtue, and if there be any praise, think on these things.",
    "I can do all things through Christ which strengtheneth me.",
  ]},
  { ch: "Psalm 91", verses: [
    "He that dwelleth in the secret place of the most High shall abide under the shadow of the Almighty.",
    "I will say of the Lord, He is my refuge and my fortress: my God; in him will I trust.",
    "Surely he shall deliver thee from the snare of the fowler, and from the noisome pestilence.",
    "He shall cover thee with his feathers, and under his wings shalt thou trust: his truth shall be thy shield and buckler.",
  ]},
];

// Repeat the passages so they fill any viewport height.
const FULL_TEXT = [...PASSAGES, ...PASSAGES, ...PASSAGES];

export const BiblePageBackground = () => {
  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      data-testid="bible-page-bg"
      aria-hidden="true"
    >
      {/* Warm aged-paper base — richer, deeper page feel */}
      <div className="absolute inset-0" style={{ backgroundColor: "#D4C5A0" }} />

      {/* Full-screen Bible page text — soft enough to read individual words
          without competing with foreground. Two-column flowing layout. */}
      <div
        className="absolute"
        style={{
          inset: 0,
          padding: "24px 20px",
          fontFamily: "'EB Garamond', 'Cormorant Garamond', Georgia, 'Times New Roman', serif",
          color: "rgba(18, 10, 2, 0.72)",
          fontSize: "13.5px",
          lineHeight: 1.52,
          columnCount: 2,
          columnGap: "26px",
          columnRule: "1px solid rgba(60, 45, 22, 0.35)",
          textAlign: "justify",
          hyphens: "auto",
          WebkitHyphens: "auto",
          letterSpacing: "0.005em",
        }}
      >
        {FULL_TEXT.map((passage, pIdx) => (
          <div
            key={pIdx}
            style={{
              breakInside: "avoid-column",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "rgba(18, 10, 2, 0.88)",
                marginBottom: "4px",
              }}
            >
              {passage.ch}
            </div>
            {passage.verses.map((v, i) => (
              <span key={i}>
                <sup
                  style={{
                    fontSize: "9px",
                    fontWeight: 700,
                    color: "rgba(18, 10, 2, 0.85)",
                    marginRight: "2px",
                    verticalAlign: "super",
                    lineHeight: 0,
                  }}
                >
                  {i + 1}
                </sup>
                {v}{" "}
              </span>
            ))}
          </div>
        ))}
      </div>

      {/* Soft cream halo behind focal column — gentler now so the page text
          remains slightly visible beneath the foreground reflection. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(56% 42% at 50% 32%, rgba(247, 240, 220, 0.88) 0%, rgba(247, 240, 220, 0.62) 35%, rgba(247, 240, 220, 0.20) 70%, rgba(239, 230, 210, 0) 100%)",
        }}
      />

      {/* Edge vignette — deeper warmth at the corners for richer page feel */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(115% 85% at 50% 50%, rgba(239, 230, 210, 0) 55%, rgba(150, 115, 65, 0.30) 100%)",
        }}
      />

      {/* Paper grain — barely-there noise to remove digital flatness */}
      <div className="absolute inset-0 paper-grain" style={{ opacity: 0.55 }} />
    </div>
  );
};
